// Update AuthContext.js to use the new authApi

import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, authApi, setAuthToken, getCurrentUserRole, getCurrentUser } from '../services/api';
import { goldApi } from '../services/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [todayGoldRate, setTodayGoldRate] = useState(null);

    useEffect(() => {
        const loadUser = async () => {
            try {
                const token = localStorage.getItem('token');
                console.log('Gold Store Auth Check - Token exists:', !!token);
                
                if (token) {
                    setAuthToken(token);
                    
                    // Try to get user data from localStorage first
                    const storedUser = getCurrentUser();
                    if (storedUser) {
                        setUser(storedUser);
                        setUserRole(storedUser.role);
                        setIsAuthenticated(true);
                        console.log('User loaded from localStorage:', storedUser.role);
                    }
                    
                    // Then verify with server
                    try {
                        const response = await authApi.getCurrentUser();
                        
                        // Handle superusers properly
                        let userRole = response.data.role;
                        if (response.data.is_superuser && response.data.role !== 'owner') {
                            userRole = 'owner';
                            console.log('User is superuser, overriding role to owner');
                        }
                        
                        const userData = {
                            id: response.data.id,
                            username: response.data.username,
                            first_name: response.data.first_name,
                            last_name: response.data.last_name,
                            email: response.data.email,
                            role: userRole,
                            role_display: response.data.role_display,
                            is_superuser: response.data.is_superuser,
                            language: response.data.language || 'en',
                            phone: response.data.phone || '',
                            address: response.data.address || '',
                            store_name: response.data.store_name || ''
                        };
                        
                        setUser(userData);
                        setUserRole(userRole);
                        setIsAuthenticated(true);
                        
                        // Update stored user data
                        localStorage.setItem('user_role', userRole);
                        localStorage.setItem('user_data', JSON.stringify(userData));
                        
                        console.log('Auth Check - User authenticated successfully:', userRole);
                        
                        // Load today's gold rate
                        await loadTodayGoldRate();
                        
                    } catch (serverError) {
                        console.log('Could not verify with server:', serverError);
                        // Keep using stored data if server verification fails
                    }
                    
                } else {
                    console.log('Auth Check - No token found');
                    setIsAuthenticated(false);
                    setUserRole(null);
                    setUser(null);
                }
            } catch (error) {
                console.error('Auth Check - Failed:', error);
                localStorage.removeItem('token');
                localStorage.removeItem('user_role');
                localStorage.removeItem('user_data');
                delete api.defaults.headers.common['Authorization'];
                setIsAuthenticated(false);
                setUser(null);
                setUserRole(null);
            } finally {
                setLoading(false);
                console.log('Auth Check - Loading complete');
            }
        };
        
        loadUser();
    }, []);

    const loadTodayGoldRate = async () => {
        try {
            const response = await goldApi.getTodayGoldRate();
            setTodayGoldRate(response.data);
        } catch (error) {
            console.warn('Could not load today\'s gold rate:', error);
        }
    };

    const login = async (username, password) => {
        try {
            console.log('Login attempt for user:', username);
            const response = await authApi.login(username, password);
            
            const token = response.data.token;
            const userData = response.data.user;
            
            if (!token || !userData) {
                throw new Error('Invalid response from server');
            }
            
            localStorage.setItem('token', token);
            localStorage.setItem('user_role', userData.role);
            localStorage.setItem('user_data', JSON.stringify(userData));
            setAuthToken(token, userData);
            
            setUser(userData);
            setUserRole(userData.role);
            setIsAuthenticated(true);
            
            // Load today's gold rate
            await loadTodayGoldRate();
            
            console.log('Login successful for user:', username, 'Role:', userData.role);
            
            return { success: true, user: userData, message: response.data.message };
        } catch (error) {
            console.error('Login failed:', error);
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.detail ||
                               error.response?.data?.non_field_errors?.[0] ||
                               error.message ||
                               'Login failed. Please check your credentials.';
            
            return { 
                success: false, 
                error: errorMessage
            };
        }
    };

    const register = async (userData) => {
        try {
            console.log('Registration attempt for user:', userData.username, 'Role:', userData.role);
            
            const registrationData = {
                username: userData.username,
                email: userData.email,
                password: userData.password,
                confirm_password: userData.confirm_password, // Note: backend expects confirm_password
                first_name: userData.first_name || '',
                last_name: userData.last_name || '',
                role: userData.role || 'sales',
                phone: userData.phone || '',
                address: userData.address || '',
                store_name: userData.store_name || '',
                language: userData.language || 'en'
            };
            
            const response = await authApi.register(registrationData);
            
            const registeredUser = response.data.user;
            const token = response.data.token;
            
            localStorage.setItem('token', token);
            localStorage.setItem('user_role', registeredUser.role);
            localStorage.setItem('user_data', JSON.stringify(registeredUser));
            setAuthToken(token, registeredUser);
            
            setUser(registeredUser);
            setUserRole(registeredUser.role);
            setIsAuthenticated(true);
            
            console.log('Registration successful for user:', userData.username, 'Role:', registeredUser.role);
            
            return { 
                success: true, 
                user: registeredUser,
                message: response.data.message || 'Registration successful'
            };
        } catch (error) {
            console.error('Registration failed:', error);
            const errorMessage = error.response?.data?.error || 
                               error.response?.data?.detail ||
                               Object.values(error.response?.data || {})[0]?.[0] ||
                               error.message ||
                               'Registration failed. Please try again.';
            
            return { 
                success: false, 
                error: errorMessage
            };
        }
    };

    const logout = async () => {
        try {
            console.log('Logging out user');
            await authApi.logout();
        } catch (error) {
            console.warn('Logout API call failed, but continuing with local logout:', error);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('user_role');
            localStorage.removeItem('user_data');
            delete api.defaults.headers.common['Authorization'];
            setUser(null);
            setUserRole(null);
            setTodayGoldRate(null);
            setIsAuthenticated(false);
        }
    };

    const updateGoldRate = (rate) => {
        setTodayGoldRate(rate);
    };

    // Role-based access control methods
    const hasRole = (role) => {
        return userRole === role;
    };

    const hasAnyRole = (roles) => {
        return roles.includes(userRole);
    };

    const isOwner = () => userRole === 'owner';
    const isManager = () => userRole === 'manager';
    const isSalesStaff = () => userRole === 'sales';
    const isAccountant = () => userRole === 'accountant';
    const isSecurityOfficer = () => userRole === 'security';

    return (
        <AuthContext.Provider value={{ 
            user, 
            loading, 
            isAuthenticated,
            userRole,
            todayGoldRate,
            login, 
            logout,
            register,
            updateGoldRate,
            // Role-based methods
            hasRole,
            hasAnyRole,
            isOwner,
            isManager,
            isSalesStaff,
            isAccountant,
            isSecurityOfficer
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};